import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { apiUtils } from '../hooks/useApi';
import {
  API_ENDPOINTS,
  LoginPayload,
  TabPermission,
} from '../services/apiEndpoints';
import AuthService from '../services/authService';
// Import directly to avoid circular dependency with UI components
import Loader from '../components/Loader';
import { getAccessibleTabs, hasTabAccess } from '../utils/permissionsUtils';
import hubspotService from '../services/hubspotService';
import {
  UserRole,
  isBrandAdmin,
  isHotelAdmin,
  isIntegrationsAdmin,
  isSuperAdmin,
} from '../constants/roles';
import { clearAllSplashData } from './SplashContext';

interface UserResponse {
  success: boolean;
  user: User;
}
// User interface
interface User {
  id: string;
  username?: string;
  agreedToTerms?: boolean;
  enterprise_name?: string;
  brand_name?: string;
  email?: string;
  name?: string;
  lastLogin?: string;
  role?: UserRole;
  hasAssignedHotelOrg?: boolean;
}

// Auth context interface
interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  isSuperAdmin: boolean;
  isIntegrationsAdmin: boolean;
  isBrandAdmin: boolean;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  tabs: string[];
  tabsLoading: boolean;
  permissions: TabPermission[]; // Full permission objects
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  hasAccessToTab: (tabName: string) => boolean;
  getPermissionLevel: (tabName: string) => string | null;
  refreshUserProfile: () => Promise<void>;
  fetchMainTabs: () => Promise<void>;
  handleSetAgreedToTerms: (agreed: boolean) => void;
  setEnterpriseName: (enterpriseName: string) => void;
  isHotelAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabs, setTabs] = useState<string[]>([]);
  const [tabsLoading, setTabsLoading] = useState(false);
  const [permissions, setPermissions] = useState<TabPermission[]>([]);

  const isUserSuperAdmin = isSuperAdmin(user?.role);
  const isUserIntegrationsAdmin = isIntegrationsAdmin(user?.role);
  const isUserBrandAdmin = isBrandAdmin(user?.role);
  const isUserHotelAdmin = isHotelAdmin(user?.role);

  // console.log('AuthProvider rendered, user:', user);

  useEffect(() => {
    if (token === null || user === null) {
      // console.log('No token or user, skipping fetchMainTabs');
      return;
    }
    fetchMainTabs();
  }, [token, user?.id]);

  // Check for existing token on app load
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      // Don't try to fetch permissions if we're on the 403 page
      // This prevents infinite loop when permissions API returns 403
      const savedToken = AuthService.getToken();
      const savedUser = await AuthService.getUser();
      if (window.location.pathname === '/403') {
        setLoading(false);
        if (savedToken && savedUser) {
          setToken(savedToken);
          setUser(savedUser);
          await fetchMainTabs();
        }
        return;
      }
      if (savedToken && savedUser) {
        setToken(savedToken);
        setUser(savedUser);
        await fetchMainTabs();
      } else if (savedToken) {
        // Token exists but no user data, fetch user profile
        await fetchUserProfile(savedToken);
      } else {
        setLoading(false);
      }
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fetchMainTabs = async () => {
    try {
      setTabsLoading(true);
      const response = await AuthService.fetchPermissions();

      // Store full permission objects
      setPermissions(response);

      // Extract accessible tab names for backward compatibility
      const tabNames = getAccessibleTabs(response);
      setTabs(tabNames);
    } catch (error: any) {
      // If it's a 403 error, clear auth state to prevent infinite loop
      if (error.response?.status === 403) {
        AuthService.removeToken();
        AuthService.removeUser();
        setToken(null);
        setUser(null);
      }

      setTabs([]);
      setPermissions([]);
    } finally {
      setTabsLoading(false);
      setLoading(false);
    }
  };

  const fetchUserProfile = async (authToken: string) => {
    try {
      AuthService.setToken(authToken);
      const response = await apiUtils.get<UserResponse>(
        API_ENDPOINTS.auth.profile
      );
      const userData = response.data;
      // Retain agreedToTerms from previous state if it exists
      const updatedUser = {
        ...userData.user,
        agreedToTerms: user?.agreedToTerms ?? userData.user.agreedToTerms,
      };
      setUser(updatedUser);
      setToken(authToken);
      await AuthService.setUser(updatedUser);
      await fetchMainTabs();
    } catch (error) {
      // Clear invalid token and user data
      AuthService.removeToken();
      AuthService.removeUser();
      setToken(null);
      setUser(null);
      setLoading(false);
    }
  };
  const login = async (username: string, password: string) => {
    try {
      const credentials: LoginPayload = { username, password };
      const response = await AuthService.login(credentials);
      const { user: userData, token: authToken } = response as any;

      // Save token and user data
      AuthService.setToken(authToken);
      await AuthService.setUser(userData);

      // Update state
      setUser(userData);
      setToken(authToken);

      // Fetch tabs after successful login
      await fetchMainTabs();
      refreshUserProfile();
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      // setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Shutdown HubSpot before logout
      hubspotService.shutdown();
      sessionStorage.clear();

      // Clear splash data for all sessions
      clearAllSplashData();

      // Call logout service (handles API call and cleanup)
      await AuthService.logout();
    } catch (error) {
      console.error('Log out failed:', error);
    } finally {
      // Clear local state regardless of API call result
      setUser(null);
      setToken(null);
      setTabs([]);
      setPermissions([]);
    }
  };

  const hasAccessToTab = (tabName: string): boolean => {
    return hasTabAccess(permissions, tabName);
  };

  const getPermissionLevel = (tabName: string): string | null => {
    const tab = permissions.find(
      t => t.main_tab.toUpperCase() === tabName.toUpperCase()
    );
    return tab ? tab.value : null;
  };

  const refreshUserProfile = async () => {
    if (!user) {
      return;
    }
    try {
      // console.log('Refreshing user profile...');
      const response = await apiUtils.get<any>(API_ENDPOINTS.auth.profile);
      // Handle different response structures: response.data.user or response.data.data.user
      const profileData = response.data.data || response.data;
      const userData = profileData.user || profileData;
      // console.log("user data ::", userData);
      // Retain agreedToTerms from previous state if it exists
      const updatedUser = {
        ...userData,
        agreedToTerms: user?.agreedToTerms ?? userData.agreedToTerms,
      };

      setUser(updatedUser);
      await AuthService.setUser(updatedUser);
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
    }
  };

  const handleSetAgreedToTerms = async (agreed: boolean) => {
    if (user) {
      const updatedUser = { ...user, agreedToTerms: agreed };
      setUser(updatedUser);
      await AuthService.setUser(updatedUser);
    }
  };

  const setEnterpriseName = async (enterpriseName: string) => {
    if (user) {
      const updatedUser = { ...user, enterprise_name: enterpriseName };
      setUser(updatedUser);
      await AuthService.setUser(updatedUser);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user && !!token,
        isSuperAdmin: isUserSuperAdmin,
        isIntegrationsAdmin: isUserIntegrationsAdmin,
        isBrandAdmin: isUserBrandAdmin,
        isHotelAdmin: isUserHotelAdmin,
        user,
        token,
        setToken,
        setUser,
        login,
        logout,
        loading,
        tabs,
        tabsLoading,
        permissions,
        hasAccessToTab,
        getPermissionLevel,
        refreshUserProfile,
        fetchMainTabs,
        handleSetAgreedToTerms,
        setEnterpriseName,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
